import React, { useEffect, useState, useRef } from 'react';

const FunMultiplicationTricks = () => {
  const [currentMultiplier, setCurrentMultiplier] = useState('11');
  const [currentNumber, setCurrentNumber] = useState(null);
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [steps, setSteps] = useState('');
  const [explanation, setExplanation] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [showRetry, setShowRetry] = useState(false);
  const inputRef = useRef();

  const sparkleTimeouts = useRef([]);

  const style = {
    body: {
      fontFamily: "'Baloo 2', cursive, sans-serif",
      background: '#fff8e7',
      margin: 0,
      padding: 20,
      color: '#4b3b2b',
      textAlign: 'center',
    },
    h1: {
      color: '#f76c6c',
      fontSize: '2.8em',
      marginBottom: 10,
      textShadow: '1px 1px 0 #fff',
    },
    label: { fontSize: '1.4em' },
    select: {
      fontSize: '1.4em',
      borderRadius: '10px',
      border: '3px solid #f76c6c',
      padding: '6px 12px',
      outline: 'none',
      cursor: 'pointer',
    },
    input: {
  fontSize: '1.4em',
  width: 250,
  fontWeight: 700,
  textAlign: 'center',
  display: 'block',
  margin: '15px auto',
  borderRadius: '10px',
  border: '3px solid #f76c6c',
  padding: '6px 12px',
  outline: 'none',
},

    button: {
      background: '#f76c6c',
      color: 'white',
      border: 'none',
      borderRadius: '14px',
      padding: '15px 40px',
      fontWeight: 900,
      marginTop: 25,
      cursor: 'pointer',
      boxShadow: '0 4px #c54a4a',
      transition: 'background 0.3s ease',
    },
    retryBtn: {
      background: '#4caf50',
      boxShadow: '0 4px #388e3c',
      display: showRetry ? 'inline-block' : 'none',
    },
    explanation: {
      maxWidth: 650,
      margin: '20px auto',
      background: '#fef5e7',
      borderRadius: '18px',
      padding: '20px 25px',
      boxShadow: '0 0 12px #f76c6c33',
      color: '#7f5a47',
      textAlign: 'left',
      whiteSpace: 'pre-wrap',
      fontSize: '1.2em',
      lineHeight: 1.5,
    },
    question: {
      fontSize: '3.5em',
      margin: '15px 0',
      fontWeight: 900,
      letterSpacing: '10px',
    },
    feedback: {
      marginTop: 30,
      fontSize: '2em',
      fontWeight: 900,
      minHeight: 70,
    },
    steps: {
      ...this?.explanation,
    },
    sparkle: {
      position: 'absolute',
      width: '14px',
      height: '14px',
      background: 'radial-gradient(circle at center, #fff, #f76c6c)',
      borderRadius: '50%',
      filter: 'drop-shadow(0 0 4px #f76c6c)',
      opacity: 0,
      animation: 'sparkleAnim 800ms ease forwards',
      pointerEvents: 'none',
      zIndex: 10,
    },
  };

  const explanationsText = {
    '11': `For two-digit number AB:
- First digit: A
- Middle digit: A + B (carry 1 if ≥10)
- Last digit: B
Example: 23 × 11 = 2 (first), 2+3=5 (middle), 3 (last) → 253`,
    '12': `Multiply by 12 = multiply by 10 + multiply by 2
Example: 15 × 12 = 150 + 30 = 180`,
    '9': `Multiply by 9 = multiply by 10 and subtract original number
Example: 23 × 9 = 230 – 23 = 207`,
    '6': `Multiply by 6 = multiply by 5 and add original number
Example: 12 × 6 = 60 + 12 = 72`,
    '7': `Multiply by 7 = multiply by 5 and add twice original number
Example: 12 × 7 = 60 + 24 = 84`,
    '8': `Multiply by 8 = multiply by 5 and add thrice original number
Example: 12 × 8 = 60 + 36 = 96`,
  };

  const calculators = {
    '11': (numStr) => {
      const A = +numStr[0], B = +numStr[1];
      const sum = A + B;
      if (sum < 10) return +(`${A}${sum}${B}`);
      return +(`${A + 1}${sum - 10}${B}`);
    },
    '12': (num) => num * 12,
    '9': (num) => num * 9,
    '6': (num) => num * 6,
    '7': (num) => num * 7,
    '8': (num) => num * 8,
  };

  const generateNumber = () => Math.floor(Math.random() * 90) + 10;

  const newQuestion = () => {
    const number = generateNumber().toString();
    setCurrentNumber(number);
    const correct = calculators[currentMultiplier](currentMultiplier === '11' ? number : +number);
    setCorrectAnswer(correct);
    setFeedback('');
    setSteps('');
    setInputValue('');
    setShowRetry(false);
    inputRef.current?.focus();
  };

  const explainWrongAnswer = (numStr, multiplier) => {
    let result = '';
    const num = +numStr;
    const A = +numStr[0], B = +numStr[1], sum = A + B;
    switch (multiplier) {
      case '11':
        result = `Let's break down ${numStr} × 11:\nFirst digit: ${A}\nSum of digits: ${A} + ${B} = ${sum}`;
        result += sum < 10
          ? `\nSo middle digit is ${sum}\nLast digit: ${B}\nAnswer: ${A}${sum}${B}`
          : `\nSum is ≥ 10, so carry 1 to the first digit.\nNew first digit: ${A + 1}\nMiddle digit: ${sum - 10}\nLast digit: ${B}\nAnswer: ${A + 1}${sum - 10}${B}`;
        break;
      case '12':
        result = `${num} × 10 = ${num * 10}\n${num} × 2 = ${num * 2}\nAdd: ${num * 12}`;
        break;
      case '9':
        result = `${num} × 10 = ${num * 10}\nSubtract ${num}: ${num * 9}`;
        break;
      case '6':
      case '7':
      case '8':
        const fiveTimes = num * 5;
        const extra = num * (multiplier - 5);
        result = `${num} × 5 = ${fiveTimes}\n${num} × ${multiplier - 5} = ${extra}\nAdd: ${fiveTimes + extra}`;
        break;
      default:
        result = 'No explanation available.';
    }
    setSteps(result);
  };

  const sparkleEffect = async () => {
    const rect = inputRef.current.getBoundingClientRect();
    for (let i = 0; i < 15; i++) {
      const x = rect.left + Math.random() * rect.width;
      const y = rect.top + Math.random() * rect.height;
      const sparkle = document.createElement('div');
      Object.assign(sparkle.style, style.sparkle, {
        left: `${x}px`,
        top: `${y}px`,
      });
      document.body.appendChild(sparkle);
      sparkleTimeouts.current.push(setTimeout(() => sparkle.remove(), 800));
      await new Promise((r) => setTimeout(r, 80));
    }
  };

  const validateAnswer = async () => {
    const userAnswer = parseInt(inputValue, 10);
    if (isNaN(userAnswer)) {
      setFeedback('Please enter a valid number!');
      return;
    }
    if (userAnswer === correctAnswer) {
      setFeedback('🎉 Correct! Great job! 🎉');
      await sparkleEffect();
    } else {
      setFeedback('Oops! Not quite right. Here’s how to do it:');
      explainWrongAnswer(currentNumber, currentMultiplier);
    }
    setShowRetry(true);
  };

  useEffect(() => {
    setExplanation(explanationsText[currentMultiplier]);
    newQuestion();
    return () => sparkleTimeouts.current.forEach(clearTimeout);
  }, [currentMultiplier]);

  return (
    <div style={style.body}>
      <style>{`
        @keyframes sparkleAnim {
          0% {opacity: 1; transform: scale(0.3) rotate(0deg);}
          50% {opacity: 1; transform: scale(1.1) rotate(180deg);}
          100% {opacity: 0; transform: scale(0.3) rotate(360deg);}
        }
      `}</style>

      <h1 style={style.h1}>Fun Multiplication Tricks!</h1>

      <label htmlFor="multiplierSelect">Choose a multiplier:</label>
      <br />
      <select
        id="multiplierSelect"
        style={style.select}
        value={currentMultiplier}
        onChange={(e) => setCurrentMultiplier(e.target.value)}
      >
        <option value="11">Multiply by 11</option>
        <option value="12">Multiply by 12</option>
        <option value="9">Multiply by 9</option>
        <option value="6">Multiply by 6</option>
        <option value="7">Multiply by 7</option>
        <option value="8">Multiply by 8</option>
      </select>

      <div style={style.explanation}>{explanation}</div>

      <div style={style.question}>{currentNumber} × {currentMultiplier}</div>

      <input
        ref={inputRef}
        style={style.input}
        type="number"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder="Your answer"
        onKeyDown={(e) => e.key === 'Enter' && validateAnswer()}
      />

      <br />

      <button style={style.button} onClick={validateAnswer}>Check Answer</button>

     

      <button style={{ ...style.button, ...style.retryBtn }} onClick={newQuestion}>Try Another Number</button>
       <div style={style.feedback}>{feedback}</div>

      <div style={style.explanation}>{steps}</div>
    </div>
  );
};

export default FunMultiplicationTricks;
